import { MutationHandlingServiceV2 } from '@akebono/core';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject } from 'rxjs';
import {
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  AuctionExtraData,
  AutoLotView,
  BoundaryStatus,
  getCalcData,
  getLotSourceFromParams,
  hasManualTranslations,
  hasTranslations,
  isKawasakiLot,
  LotData,
  mergeSavedLotToAutoLotView,
  ReactiveLotData,
} from 'src/app/const';
import {
  Lot,
  LotPhotoRequestGQL,
  LotTypeEnum,
  UserLotGQL,
  UserLotQuery,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import { MotoLotQuery } from 'src/app/modules/graphql/service/graphql-cars-default-service';
import { SanctionsService } from 'src/app/services/sanctions.service';

import { environment } from '../../../../../environments/environment';
import { AuctionService } from '../../../../services/auction.service';
import { LimitsService } from '../../../../services/limits.service';
import { LotViewsService } from '../../../../services/lot-views.service';
import { pickCurrentTradingTimeShift } from '../../../../utils/pick-current-trading-time-shift.util';

@Component({
  selector: 'app-moto-lot',
  templateUrl: './moto-lot.component.html',
  styleUrls: ['./moto-lot.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MotoLotComponent implements OnInit, OnDestroy {
  data$: ReactiveLotData = of({});
  auction$: Observable<AuctionExtraData>;
  loading$ = new BehaviorSubject<boolean>(true);
  refetch$ = new Subject<void>();

  BoundaryStatus = BoundaryStatus;

  activeSlide = 1;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private limitsService: LimitsService,
    private mhs: MutationHandlingServiceV2,
    private lotViewsService: LotViewsService,
    private auctionService: AuctionService,
    private lotPhotoRequestGQL: LotPhotoRequestGQL,
    private userLotGQL: UserLotGQL,
    private sanctionsService: SanctionsService,
  ) {}

  ngOnInit(): void {
    this.data$ = combineLatest([
      this.route.params,
      this.refetch$.asObservable().pipe(startWith(true)),
    ]).pipe(
      tap(() => this.loading$.next(true)),
      map(([params]) => ({
        lotId: params.lot,
        lotType: LotTypeEnum.Moto,
        lotSource: getLotSourceFromParams(params),
      })),
      switchMap((initData) =>
        this.userLotGQL
          .fetch(
            {
              id: initData.lotId,
              lotSource: initData.lotSource,
              subgroup: initData.lotType,
              siteDomain: environment.lotSiteDomain,
            },
            { fetchPolicy: 'network-only' },
          )
          .pipe(
            map((userResult) => ({
              initData,
              userResult,
            })),
          ),
      ),
      tap(({ initData }) =>
        this.lotViewsService.increment(initData.lotId, initData.lotType, initData.lotSource),
      ),
      switchMap((data) =>
        iif(
          () => Boolean(data.userResult.data.lot),
          of(data).pipe(
            map((data) => ({
              ...data,
              lot: this.compileAutoLotView(
                'const',
                null,
                data.userResult.data.lot,
                data.userResult.data.lotViewsCount,
              ),
            })),
            map((data) => ({
              ...data.initData,
              lot: data.lot,
              savedLot: data.userResult.data?.lot,
              bids: data.userResult.data?.lot?.bids ?? [],
              icons: data.userResult.data?.lot?.icons ?? [],
              currentUser: data.userResult.data.currentUser,
              currentUserLastBid: data.userResult.data?.lot?.currentUserLastBid,
              currentUserLimitsOverrideAvailable:
                data.userResult.data?.lot?.currentUserLimitsOverrideAvailable,
              hasBestBid: data.userResult.data?.lot?.hasBestBid,
              bestBid: data.userResult.data?.lot?.bestBid,
              currentUserLotMarks: data.userResult.data?.lot?.currentUserLotMarks || [],
              translations: data.userResult.data?.lot?.translations,
              hasTranslations: hasTranslations(data.userResult.data?.lot?.translations),
              hasManualTranslations: hasManualTranslations(data.userResult.data?.lot?.translations),
              limits: this.limitsService.getLimits(data.lot, data.userResult.data?.lot as Lot),
              isUsingSavedImages: Boolean(data.userResult.data.lot),
              isSanctioned: this.sanctionsService.isLotSanctioned(
                data.lot,
                data.userResult.data.currentUser,
              ),
              isKawasakiLot: isKawasakiLot(data?.lot?.company),
              calcData: getCalcData(
                data.lot,
                'moto',
                data.userResult.data?.lot?.currentUserLastBid?.amount,
              ),
            })),
            tap((data) => {
              const { bid, auctionName, company, modelName, frame } = data.lot;
              this.title.setTitle(
                `${bid} - ${auctionName} - ${company} ${modelName} ${frame} | ECar JP`,
              );
              this.requestInspectionNotes(data as LotData);
            }),
          ),
          of(data.initData),
        ),
      ),
      tap(() => this.loading$.next(false)),
      shareReplay(1),
    );

    this.auction$ = this.data$.pipe(
      map((lotData) => ({
        lot: lotData?.lot,
        auctionName: lotData?.lot?.auctionName,
      })),
      filter(
        (data): data is { lot: AutoLotView; auctionName: string } =>
          !!data.lot && !!data.auctionName,
      ),
      switchMap(({ lot, auctionName }) =>
        this.auctionService.getAliasAuctionByName(auctionName).pipe(
          map((auction) => ({
            currentTradingTimeShift: pickCurrentTradingTimeShift(
              auction?.currentTradingTimeShifts,
              lot,
            ),
          })),
        ),
      ),
      shareReplay(1),
    );
  }

  compileAutoLotView(
    dumpVersion: string,
    motoLot?: MotoLotQuery['motoLot'],
    savedLot?: UserLotQuery['lot'],
    viewsCount?: UserLotQuery['lotViewsCount'],
  ): AutoLotView {
    let lot: AutoLotView;

    if (motoLot) {
      lot = {
        ...motoLot,
        jupiterLotData: savedLot.jupiterLotData,
        images: motoLot.images.split('#'),
        // TODO: возможно временно убрано по просьбе Vitalii Kubar
        // .map((url) => url.replace('https://p3.aleado.com/', environment.imageProxy)),
        auctionName: motoLot.motoAuction.name,
        companyId: motoLot.companyRef,
        modelId: motoLot.modelNameRef,
        startPrice: motoLot.startPrice * 1000,
        dumpVersion,
        viewsCount: viewsCount.viewsCount,
        vin: motoLot.vin || (motoLot.frame ? motoLot.frame + '-' : ''),
      };
    }

    if (savedLot) {
      lot = this.updateAutoLotViewBySavedData(lot, savedLot);
    }

    return lot;
  }

  updateAutoLotViewBySavedData(lot: AutoLotView, savedLot?: UserLotQuery['lot']): AutoLotView {
    try {
      lot = mergeSavedLotToAutoLotView(lot, savedLot);
    } catch (e) {
      console.error(e);
    }

    lot.productionDate = savedLot.productionDate;
    lot.vin = savedLot.vin || lot?.vin;
    lot.images = lot?.images?.length > savedLot?.images?.length ? lot?.images : savedLot?.images;
    lot.negotiationsPrice = savedLot.negotiationsPrice;
    lot.currentUserLotNote = savedLot.currentUserLotNote;
    lot.jupiterLotData = savedLot.jupiterLotData;
    lot.kawasakiLotData = savedLot.kawasakiLotData;

    return lot;
  }

  scrollToTranslations(): void {
    document.getElementById('lotTranslations').scrollIntoView({ behavior: 'smooth' });
  }

  private requestInspectionNotes(lotData: LotData): void {
    if (!lotData.lot.auctionName.includes('BDS') || lotData.lot.jupiterLotData) {
      return;
    }

    this.mhs
      .mutate(
        this.lotPhotoRequestGQL,
        {
          input: {
            lotId: lotData.lot.id,
            lotType: lotData.lotType,
            lotSource: lotData.lotSource,
          },
        },
        {
          renderSuccess: false,
          renderError: false,
        },
      )
      .data.pipe(first(), takeUntil(this.destroy$))
      .subscribe(() => this.refetch$.next());
  }

  goToFirstSlide(isUsingSavedImages: boolean): void {
    if (!isUsingSavedImages) {
      this.activeSlide = 1;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
